import { useState, useLayoutEffect, useRef, useEffect, useCallback } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { TableToolbar, SimpleTooltip } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";

import VehicleAlerts from "../../../../components/vehicleAlerts";
import PeriodicReviews from "../../../../components/periodicReviews";
import Inspections from "../../../../components/inspections";
import { fixedColumns, scrollableColumns } from "./tableConfigs";
import { getTableLabels } from "./tableLabels";
import ExportProgressDialog from "../../../../components/exportProgressDialog";
import MaintenanceGroupSidebar from "../../../../components/maintenanceGroupSidebar";
import { getMaintenanceGroupLabelByValue } from "../../../../components/maintenanceGroupSidebar/components/enums/maintenanceGroupEnum";

import {
  Asset,
  AssetConnectStatus,
  AssetManualMetrics,
  AssetPlanList,
} from "../../../../models/asset";

import ToSchedule from "./toSchedule";
import NextScheduling from "./nextScheduling";

import { ManualDialog } from "../dialogs/manualDialog";
import { EditOrdometerHourmeterDialog } from "../dialogs/editOrdometerHourmeterDialog";
import ContractDetailsSidebar from "../../../../components/contractDetailsSidebar";
import { UserRoleEnum } from "../../../../enums/role";

import {
  assetHasMetrics,
  assetHasOneMetric,
  getAssetRioPlan,
  getUptimeAsset,
} from "../../../../services/assetService";

import { getSchedulingCheckupDisplayName } from "../../../../utils/checkupTypeLabel";
import { getEffectiveOdometer, getEffectiveHourmeter } from "../../../../utils/assetUtils";

import useTableScroll from "../../../../hooks/useTableScroll";
import useAssetAlert from "../../../../hooks/useAssetAlert";
import useAssetCampaign from "../../../../hooks/useAssetCampaign";
import useAssetManuals from "../../../../hooks/useAssetManuals";
import useAssetPeriodicReview from "../../../../hooks/useAssetPeriodicReviews";
import { useRioPlansCache } from "../../../../hooks/useAssets";
import { useChassisCopy } from "../../../../hooks/useChassisCopy";

import {
  renderTableCaption,
  renderTableHead,
} from "../../../../utils/tableUtils";

import {
  fetchMaintenance,
  IAssetSummary,
} from "../../../../api/fetchMaintenance";
import { fetchComplementaryAssetsDetails } from "../../../../api/fetchAssets";
import { fetchCheckupsByChassisList } from "../../../../api/fetchCheckup";
import { fetchAssetsByRioPlans } from "../../../../api/fetchBigNumbers";
import { exportAssetsToExcel } from "../../../../utils/exportAssetsToExcel";
import { calcProgress } from "../../../../utils/exportProgress";

import "../styles.scss";

interface IMaintenanceTableProps {
  assets: Asset[];
  uptimeAssets: AssetManualMetrics[];
  assetsPlans: AssetPlanList[];
  assetsConnectionStatus: AssetConnectStatus[];
  availableToScheduleAssetIds: string[];
  allSummaryAssets: IAssetSummary[];
  isSidebarOpen: boolean;
  onAssetUpdate?: (assetId: string) => void;
  handleUpdateAssets: () => Promise<void>
}

const MaintenanceTable = (props: IMaintenanceTableProps) => {
  const intl = useIntl();
  const tableLabels = getTableLabels(intl);
  const [isExporting, setIsExporting] = useState(false);
  const [contractDetailsAsset, setContractDetailsAsset] = useState<Asset | null>(null);
  const [maintenanceGroupSidebarAsset, setMaintenanceGroupSidebarAsset] = useState<Asset | null>(null);
  const [showContractDetailsDialog, setShowContractDetailsDialog] = useState(false);
  const [showMaintenanceGroupSidebarDialog, setShowMaintenanceGroupSidebarDialog] = useState(false);
  const navigate = useNavigate();
  const [, setStateUpdatedAt] = useState(new Date().getTime());
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressStage, setProgressStage] = useState<
    | "maintenance"
    | "complementary"
    | "checkups"
    | "rioplans"
    | "writing"
    | "done"
    | "error"
  >("maintenance");
  const [progressPct, setProgressPct] = useState(0);
  const [progressDetail, setProgressDetail] = useState<string | undefined>();
  const [progressError, setProgressError] = useState<string | undefined>();
  const isResponsiveView = useMediaQuery("(max-width: 1024px)");
  const { copyChassis, isCopied } = useChassisCopy();

  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const fixedTableHeaderRef = useRef<HTMLTableSectionElement>(null);

  const [showStickyScrollableHeader, setShowStickyScrollableHeader] = useState(false);
  const [scrollableHeaderScrollLeft, setScrollableHeaderScrollLeft] = useState(0);
  const scrollableTableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const scrollableHeaderContainerRef = useRef<HTMLDivElement>(null);

  const [showResponsiveStickyHeader, setShowResponsiveStickyHeader] = useState(false);
  const responsiveTableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const [responsiveHeaderScrollLeft, setResponsiveHeaderScrollLeft] = useState(0);
  const responsiveTableContainerRef = useRef<HTMLDivElement>(null);
  const responsiveScrollableRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const fixedTableContainerRef = useRef<HTMLDivElement>(null);
  const [fixedTableWidth, setFixedTableWidth] = useState(0);
  
  // Handler para atualização de odômetro/horímetro
  const handleAssetUpdate = useCallback((assetId: string) => {
    setStateUpdatedAt(new Date().getTime());
    if (props.onAssetUpdate) {
      props.onAssetUpdate(assetId);
    }
  }, [props.onAssetUpdate]);


  const getThresholds = useCallback(() => {
    return {
      show: 10,
      hide: 80,
    };
  }, []);

  const checkResponsiveHeaderPosition = useCallback(() => {
    if (!responsiveTableHeaderRef.current) return;

    const rect = responsiveTableHeaderRef.current.getBoundingClientRect();

    setShowResponsiveStickyHeader((prev) => {
      if (!prev && rect.top <= -10) {
        return true;
      } else if (prev && rect.top >= 10) {
        return false;
      }
      return prev;
    });
  }, []);

  const handleResponsiveScroll = useCallback(() => {
    if (showResponsiveStickyHeader && responsiveScrollableRef.current) {
      setResponsiveHeaderScrollLeft(responsiveScrollableRef.current.scrollLeft);
    }
  }, [showResponsiveStickyHeader]);

  useEffect(() => {
    const checkHeaderPosition = () => {
      if (!fixedTableHeaderRef.current) return;

      const rect = fixedTableHeaderRef.current.getBoundingClientRect();
      const { show, hide } = getThresholds();
      
      setShowStickyHeader((prev) => {
        if (!prev && rect.bottom <= show) {
          return true;
        } else if (prev && rect.bottom >= hide) {
          return false;
        }
        return prev;
      });
    };

    const handleScroll = () => {
      if (showStickyHeader && fixedTableHeaderRef.current) {
        const rect = fixedTableHeaderRef.current.getBoundingClientRect();
        const { hide } = getThresholds();
        if (rect.bottom >= hide) {
          setShowStickyHeader(false);
          return;
        }
      }
      checkHeaderPosition();
    };

    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (hasScroll && element.scrollHeight > element.clientHeight) {
        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const scrollableContainer = findScrollableParent(
      fixedTableHeaderRef.current?.parentElement || null
    );

    const elements: (Window | Document | HTMLElement)[] = [window, document];
    if (scrollableContainer) {
      elements.push(scrollableContainer as HTMLElement);
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleScroll, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleScroll, { passive: true });
    });

    checkHeaderPosition();
    const interval = setInterval(() => {
      checkHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleScroll);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleScroll);
      });
    };
  }, [showStickyHeader, getThresholds]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const el = fixedTableContainerRef.current;
    if (!el) return;
    const measureWidth = () => {
      const tableEl = el.querySelector<HTMLTableElement>('table');
      return Math.max(
        el.getBoundingClientRect().width,
        tableEl ? tableEl.getBoundingClientRect().width : 0
      );
    };
    const update = () => setFixedTableWidth(measureWidth());
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    const tableEl = el.querySelector('table');
    if (tableEl) observer.observe(tableEl);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const el = fixedTableContainerRef.current;
    if (!el) return;
    let rafId: number;
    const startTime = performance.now();
    const ANIM_DURATION = 450;
    const update = (now: number) => {
      const tableEl = el.querySelector<HTMLTableElement>('table');
      const width = Math.max(
        el.getBoundingClientRect().width,
        tableEl ? tableEl.getBoundingClientRect().width : 0
      );
      setFixedTableWidth(width);
      if (now - startTime < ANIM_DURATION) {
        rafId = requestAnimationFrame(update);
      }
    };
    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [props.isSidebarOpen]);

   const getStickyHeaderWidth = (width: number, isSidebarOpen: boolean): string => {
    if (width <= 1366) {
      return isSidebarOpen ? "76.5%" : "104%"; 
    }
    if(width >= 1440 && width <= 1600) {
      return isSidebarOpen ? "71%" : "95%"; 
    }
    if (width >= 1601 && width <= 1920) {
      return isSidebarOpen ? "77.7%" : "95.6%"; 
    }
    if (width >= 1921 && width <= 2560) {
      return isSidebarOpen ? "82.7%" : "96.2%"; 
    }
    return "99%"; 
  };

  // Efeito para o header responsivo
  useEffect(() => {
    if (!isResponsiveView) return;

    const handleResponsiveScrollLocal = () => {
      if (showResponsiveStickyHeader && responsiveTableHeaderRef.current) {
        const rect = responsiveTableHeaderRef.current.getBoundingClientRect();
        if (rect.top >= 10) {
          setShowResponsiveStickyHeader(false);
          return;
        }
      }
      checkResponsiveHeaderPosition();
    };

    const elements: (Window | Document | HTMLElement)[] = [window, document];

    // Adicionar listener para scroll horizontal da tabela responsiva
    if (responsiveScrollableRef.current) {
      responsiveScrollableRef.current.addEventListener('scroll', handleResponsiveScroll, { passive: true });
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleResponsiveScrollLocal, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleResponsiveScrollLocal, { passive: true });
    });

    checkResponsiveHeaderPosition();
    const interval = setInterval(() => {
      checkResponsiveHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleResponsiveScrollLocal);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleResponsiveScrollLocal);
      });
      if (responsiveScrollableRef.current) {
        responsiveScrollableRef.current.removeEventListener('scroll', handleResponsiveScroll);
      }
    };
  }, [isResponsiveView, showResponsiveStickyHeader, checkResponsiveHeaderPosition, handleResponsiveScroll]);

  const checkScrollableHeaderPosition = useCallback(() => {
    
    if (!scrollableTableHeaderRef.current) {
      return;
    }
    
    const rect = scrollableTableHeaderRef.current.getBoundingClientRect();
    const { show, hide } = getThresholds();
    
   
    
    setShowStickyScrollableHeader((prev) => {
      if (!prev && rect.bottom <= show) {
        return true;
      } else if (prev && rect.bottom >= hide) {
        return false;
      }
      return prev;
    });
    
    if (showStickyScrollableHeader) {
      const scrollableTable = document.querySelector('.original-scrollable-table');
      if (scrollableTable) {
        setScrollableHeaderScrollLeft(scrollableTable.scrollLeft || 0);
      }
    }
  }, [showStickyScrollableHeader, getThresholds]);

  const handleScrollableScroll = useCallback(() => {
    checkScrollableHeaderPosition();
    
    const scrollableTable = document.querySelector('.original-scrollable-table') as HTMLElement;
    if (scrollableTable) {
      setScrollableHeaderScrollLeft(scrollableTable.scrollLeft);
    }
  }, [checkScrollableHeaderPosition]);

  useLayoutEffect(() => {
    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (hasScroll && element.scrollHeight > element.clientHeight) {
        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const scrollableContainer = findScrollableParent(
      scrollableTableHeaderRef.current?.parentElement || null
    );
    

    const elements: (Window | Document | HTMLElement)[] = [window, document];
    if (scrollableContainer) {
      elements.push(scrollableContainer as HTMLElement);
    }

    // Adicionar listener para scroll horizontal da tabela scrollável
    const scrollableTable = document.querySelector('.original-scrollable-table') as HTMLElement;
    if (scrollableTable) {
      scrollableTable.addEventListener('scroll', handleScrollableScroll, { passive: true });
    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {
      el.addEventListener("scroll", handleScrollableScroll, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleScrollableScroll, { passive: true });
    });

    checkScrollableHeaderPosition();
    const interval = setInterval(() => {
      checkScrollableHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleScrollableScroll);
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleScrollableScroll);
      });
      if (scrollableTable) {
        scrollableTable.removeEventListener('scroll', handleScrollableScroll);
      }
    };
  }, [handleScrollableScroll, checkScrollableHeaderPosition]);

  const exportToExcel = async () => {
    setProgressOpen(true);
    setProgressError(undefined);
    setProgressStage("maintenance");
    setProgressPct(0);

    try {
      await exportAssetsToExcel({
        allSummaryAssets: props.allSummaryAssets,
        assets: props.assets,
        assetsPlans: props.assetsPlans,
        assetsConnectionStatus: props.assetsConnectionStatus,
        fetchMaintenance,
        fetchComplementaryAssetsDetails,
        fetchCheckupsByChassisList,
        fetchAssetsByRioPlans, // << use a função que você mostrou
        getAssetRioPlan, // fallback, se quiser
        onChunk: (stage, index, total) => {
          // converte o stage do util pro nosso tipo
          const s = stage as any;
          setProgressStage(s);
          setProgressDetail(total ? `${index}/${total}` : undefined);
          const pct = calcProgress(
            s === "rioplans" ||
              s === "writing" ||
              s === "maintenance" ||
              s === "complementary" ||
              s === "checkups"
              ? s
              : "writing",
            Math.min(index, total),
            total || 1
          );
          setProgressPct(pct);
        },
      });
      setProgressPct(100);
      setProgressStage("done");
      setProgressDetail(undefined);
    } catch (err: any) {
      setProgressStage("error");
      setProgressError(err?.message || intl.formatMessage({ id: "intl-msg:monitoring.export.error" }));
    }
  };

  const {
    hasAlert,
    hasCriticalAlert,
    getCriticalAlertCount,
    handleAlertSidebar,
    handleCloseAlertSidebar,
    alertSidebar,
    assetAlerts,
  } = useAssetAlert(props.assets.map((asset) => asset.id));
  const {
    assetManuals,
    handleAssetManualsSidebar,
    handleCloseAssetManualsSidebar,
    assetManualsDialog,
    assetManualsLoading,
    assetManualsChassis,
  } = useAssetManuals();
  const {
    hasCampaign,
    handleCampaignsSidebar,
    handleCloseCampaignSidebar,
    campaignSidebar,
    campaigns,
    campaignsAssetId,
  } = useAssetCampaign();
  const {
    handlePeriodicReviewSidebar,
    handleClosePeriodicReviewSidebar,
    periodicReviewSidebar,
    assetPeriodicReviews,
  } = useAssetPeriodicReview();
  const { scrolledClass, scrollableTableRef } = useTableScroll();

  const renderSchedulingData = (columnValue: string, asset: Asset) => {
    return (
      columnValue === "agendamento" &&
      asset.schedulings?.previous && (
        <ToSchedule
          asset={asset}
          hasMetrics={
            assetHasOneMetric(asset, props.uptimeAssets) &&
            props.availableToScheduleAssetIds.includes(asset.id)
          }
        />
      )
    );
  };

  const renderChassisData = (columnValue: string, asset: Asset) => {
    const handleChassisClick = async () => {
      // Copiar automaticamente o chassis para a área de transferência
      await copyChassis(asset.chassis);
      
      // Abrir o sidebar de manuais
      handleAssetManualsSidebar(asset.chassis);
    };

    return (
      columnValue === "chassi" && (
        <div className="chassi-info relative">
          <button
            type="button"
            onClick={handleChassisClick}
            className="flex items-center gap-2"
          >
            <span className="chassi-info">{asset.chassis}</span>
          </button>
          
          {isCopied(asset.chassis) && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-green-600 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap">
              Item Copiado
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-green-600"></div>
            </div>
          )}
        </div>
      )
    );
  };

  const renderOdometerData = (columnValue: string, asset: Asset) => {
    const effectiveOdometer = getEffectiveOdometer(
      asset,
      getUptimeAsset(asset, props.uptimeAssets)
    );
    const showEditButton = !effectiveOdometer || effectiveOdometer.source !== "asset";

    return (
      columnValue === "odometro" && (
        <div className="text-center">
          {effectiveOdometer ? (
            <div>
              <div className="truncate">
                {effectiveOdometer.odometer.toLocaleString("pt-BR") + " km"}
                {showEditButton && (
                  <span className="margin-left-10">
                    <EditOrdometerHourmeterDialog
                      type="odometer"
                      asset={asset}
                      onClose={handleAssetUpdate}
                    />
                  </span>
                )}
              </div>
              {effectiveOdometer.lastOdometerUpdate ? (
                <SimpleTooltip
                  content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${effectiveOdometer.lastOdometerUpdate.toLocaleDateString(
                    "pt-BR"
                  )}`}
                >
                  <div className="text-size-11 margin-top-5 text-color-highlight !line-clamp-2">
                    {intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })}{" "}
                    {effectiveOdometer.lastOdometerUpdate.toLocaleDateString("pt-BR")}
                  </div>
                </SimpleTooltip>
              ) : null}
            </div>
          ) : (
            <EditOrdometerHourmeterDialog
              type="odometer"
              asset={asset}
              onClose={handleAssetUpdate}
            />
          )}
        </div>
      )
    );
  };

  const renderHourmeterData = (columnValue: string, asset: Asset) => {
    const effectiveHourmeter = getEffectiveHourmeter(
      asset,
      getUptimeAsset(asset, props.uptimeAssets)
    );
    const showEditButton = !effectiveHourmeter || effectiveHourmeter.source !== "asset";

    return (
      columnValue === "horimetro" && (
        <div className="text-center">
          {effectiveHourmeter ? (
            <div>
              <div className="truncate">
                {effectiveHourmeter.hourmeter.toLocaleString("pt-BR") + " h"}
                {showEditButton && (
                  <span className="margin-left-10">
                    <EditOrdometerHourmeterDialog
                      type="hourmeter"
                      asset={asset}
                      onClose={handleAssetUpdate}
                    />
                  </span>
                )}
              </div>
              {effectiveHourmeter.lastHourmeterUpdate ? (
                <SimpleTooltip
                  content={`${intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })} ${effectiveHourmeter.lastHourmeterUpdate.toLocaleDateString(
                    "pt-BR"
                  )}`}
                >
                  <div className="text-size-11 margin-top-5 text-color-highlight !line-clamp-2">
                    {intl.formatMessage({ id: "intl-msg:monitoring.table.updatedAt" })}{" "}
                    {effectiveHourmeter.lastHourmeterUpdate.toLocaleDateString("pt-BR")}
                  </div>
                </SimpleTooltip>
              ) : null}
            </div>
          ) : (
            <EditOrdometerHourmeterDialog
              type="hourmeter"
              asset={asset}
              onClose={handleAssetUpdate}
            />
          )}
        </div>
      )
    );
  };

  const getLastReview = (asset: Asset) => {
    if (!asset.schedulings?.previous || asset.schedulings.previous.length === 0) {
      return null;
    }
    const reviewsWithData = asset.schedulings.previous
      .filter(review => review.date && review.checkup?.name)
      .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());
    return reviewsWithData[0] || null;
  };

  const renderRioPlan = (asset: Asset) => {
    let plans;

    const cache = useRioPlansCache.getState().cache;
    if (Object.keys(cache).length !== 0) {
      const plansList = [];
      for (const [cacheKey, cachedData] of Object.entries(cache)) {
        if (!useRioPlansCache.getState().isCacheValid(cacheKey)) continue;
        const plansData = cachedData.data;
        for (const [planName, assetIds] of Object.entries(plansData)) {
          if (assetIds.includes(asset.id)) plansList.push(planName);
        }
      }
      plans = plansList.join(', ');
    }

    return <div className="text-center">
      {plans ? (
        <span className="text-color-gray line-clamp-2">{plans}</span>
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            window.open(
              "https://latam-marketplace.rio.cloud/customerCenter/services",
              "_blank"
            )
          }
        >
          <span
            className="rioglyph rioglyph-shopping-cart"
            aria-hidden="true"
          ></span>
        </button>
      )}
    </div>

  };

  const renderStickyScrollableHeader = () => {
    if (!showStickyScrollableHeader) {
      return null;
    }
    
const getScrollableLeft = (): number => {
  const left = fixedTableContainerRef.current?.getBoundingClientRect().left ?? 0;
  return left + fixedTableWidth;
};
    return (
      <div
        ref={scrollableHeaderContainerRef}
        className="scrollable-sticky-header"
        style={{
          position: 'fixed',
          top: 50,
          zIndex: 5,
          left: getScrollableLeft(),
          right: 28,
          display: 'block',
          opacity: 1,
          transition: 'opacity 0.2s ease-out',
          overflow: 'hidden'
        }}
      >
        <div 
          className="scrollable-table"
          style={{
            transform: `translateX(${-scrollableHeaderScrollLeft}px)`,
            overflow: 'visible',
            marginLeft: 8,
            width: 'auto'
          }}
        >
          <table 
            className="table table-layout-fixed table-column-overflow-hidden table-bordered table-head-filled"
            style={{ minWidth: "1650px", width: "auto" }}
          >
            <colgroup>
              
              {scrollableColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead>
              <tr>
                
                {scrollableColumns.map((column) =>
                  renderTableHead(
                    column.value,
                    tableLabels[column.value as keyof typeof tableLabels],
                    "veiculo",
                    () => { },
                  )
                )}
              </tr>
            </thead>
          </table>
        </div>
      </div>
    );
  };

  const renderResponsiveStickyHeader = () => {
    if (!showResponsiveStickyHeader) return null;
    
    // Calcular largura mínima total baseada nas configurações das colunas
    const totalMinWidth = [...fixedColumns, ...scrollableColumns].reduce((acc, column) => {
      const widthToUse = column.details?.width && column.details.width > 0 
        ? column.details.width 
        : column.details?.defaultWidth || 100;
      return acc + widthToUse;
    }, 0);

    return (
      <div
        className="responsive-sticky-header"
        style={{
          position: 'fixed',
          top: 0,
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ transform: `translateX(${-responsiveHeaderScrollLeft}px)` }}>
          <table 
            className="table table-head-filled table-bordered min-w-full responsive-header-table"
            style={{ 
              tableLayout: "fixed",
              width: "auto",
              minWidth: `${totalMinWidth}px`
            }}
          >
          <colgroup>
            {fixedColumns.map((column) =>
              renderTableCaption(column.value, column.details)
            )}
            {scrollableColumns.map((column) =>
              renderTableCaption(column.value, column.details)
            )}
          </colgroup>
          <thead>
            <tr>
              {fixedColumns.map((column) =>
                renderTableHead(
                  column.value,
                  tableLabels[column.value as keyof typeof tableLabels],
                  "veiculo",
                  () => { },
                )
              )}
              {scrollableColumns.map((column) =>
                renderTableHead(
                  column.value,
                  tableLabels[column.value as keyof typeof tableLabels],
                  "veiculo",
                  () => { },
                )
              )}
            </tr>
          </thead>
        </table>
        </div>
      </div>
    );
  };

  const renderModals = () => (
    <>
      {alertSidebar && (
        <VehicleAlerts
          alerts={assetAlerts}
          closeSidebar={handleCloseAlertSidebar}
        />
      )}

      <ManualDialog
        show={assetManualsDialog}
        chassis={assetManualsChassis}
        close={handleCloseAssetManualsSidebar}
        manuals={assetManuals}
        loading={assetManualsLoading}
      />

      {campaignSidebar && (
        <Inspections
          inspectionsData={{ list: campaigns?.list }}
          closeSidebar={handleCloseCampaignSidebar}
          assetId={campaignsAssetId!}
          userRole="TOWER"
        />
      )}
      
      {(() => {
        return periodicReviewSidebar && (
          <PeriodicReviews
            dialog={periodicReviewSidebar}
            asset={assetPeriodicReviews}
            closeSidebar={handleClosePeriodicReviewSidebar}
            scheduleIds={[
              ...(assetPeriodicReviews?.schedulings?.next?.map(
                (review) => review.id
              ) ?? []),
              ...(assetPeriodicReviews?.schedulings?.previous?.map(
                (review) => review.id
              ) ?? []),
            ]}
            userRole="TOWER"
          />
        );
      })()}
      
      <ExportProgressDialog
        show={progressOpen}
        stage={progressStage}
        progress={progressPct}
        detail={progressDetail}
        errorMessage={progressError}
        onClose={() => setProgressOpen(false)}
      />

      <ContractDetailsSidebar
        asset={contractDetailsAsset}
        show={showContractDetailsDialog}
        userRole={UserRoleEnum.TOWER}
        setMaintenanceGroupSidebarAsset={setMaintenanceGroupSidebarAsset}
        setShowMaintenanceGroupSidebarDialog={setShowMaintenanceGroupSidebarDialog}
        onClose={() => {
          setShowContractDetailsDialog(false);
          setContractDetailsAsset(null);
        }}
      />

      <MaintenanceGroupSidebar
        asset={maintenanceGroupSidebarAsset}
        show={showMaintenanceGroupSidebarDialog}
        onClose={() => {
            setShowMaintenanceGroupSidebarDialog(false)
        }}
        onSuccess={() => {
          props.handleUpdateAssets()
        }}
      />
    </>
  );

  if (isResponsiveView) {
    // Calcular largura mínima total baseada nas configurações das colunas
    const totalMinWidth = [...fixedColumns, ...scrollableColumns].reduce((acc, column) => {
      const widthToUse = column.details?.width && column.details.width > 0 
        ? column.details.width 
        : column.details?.defaultWidth || 100;
      return acc + widthToUse;
    }, 0);

    return (
      <div id="TableCommonDemo">
        {renderResponsiveStickyHeader()}
        <div className="responsive-sticky-header tables-row" ref={responsiveTableContainerRef} style={{ position: 'relative' }}>
          <div className="overflow-x-auto " ref={responsiveScrollableRef} >
            <table 
              className="table table-head-filled table-bordered min-w-full"
              style={{ 
                tableLayout: "fixed",
                width: "auto",
                minWidth: `${totalMinWidth}px`
              }}
            >
              <colgroup>
                {fixedColumns.map((column) =>
                  renderTableCaption(column.value, column.details)
                )}
                {scrollableColumns.map((column) =>
                  renderTableCaption(column.value, column.details)
                )}
              </colgroup>
              <thead ref={responsiveTableHeaderRef}>
                <tr>
                {fixedColumns.map((column) => renderTableHead(
                  column.value,
                  tableLabels[column.value as keyof typeof tableLabels],
                  "veiculo",
                  () => { },
                )
                )}
                {scrollableColumns.map((column) => renderTableHead(
                  column.value,
                  tableLabels[column.value as keyof typeof tableLabels],
                  "veiculo",
                  () => { },
                )
                )}
              </tr>
            </thead>
            <tbody>
              {props.assets.map((asset, assetIndex) => (
                <tr key={assetIndex}>
                  {fixedColumns.map((column, colIndex) => (
                    <td key={`${column.value}-${assetIndex}-${colIndex}`} data-field={column.label}>
                      {renderSchedulingData(column.value, asset)}

                      {column.value === "veiculo" && (
                        <div className="warning-vehicle">
                          <span className="vehicle-name" title={asset.name}>
                            {asset.name ?? asset.model}
                          </span>
                          {hasAlert(asset.id) ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-action text-size-h6"
                              onClick={() => handleAlertSidebar(asset.id)}
                            >
                              <span
                                className="rioglyph rioglyph-warning-sign"
                                aria-hidden="true"
                              ></span>
                            </button>
                          ) : null}
                        </div>
                      )}

                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                   {column.value === "licensePlate" && (
                    <div>
                      {asset.licensePlate === '-' ? (
                        <span>-</span>  
                      ) : asset.licensePlate ? (
                        <SimpleTooltip content={asset.licensePlate} placement="top">
                          <div className="truncate">{asset.licensePlate}</div>
                        </SimpleTooltip>
                      ) : (
                        <div className="animate-pulse">
                          <div className="w-3/4 h-4 bg-gray-300 rounded" />
                        </div> 
                      )}
                    </div>
                  )}
                      {column.value === "accountName" && (
                        asset.accountName ? (
                          <SimpleTooltip content={asset.accountName} placement="top">
                            <div className="line-clamp-3">{asset.accountName}</div>
                          </SimpleTooltip>
                        ) : (
                          <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        )
                      )}
                    </td>
                  ))}
                  {scrollableColumns.map((column, colIndex) => (
                    <td key={`${column.value}-${assetIndex}-${colIndex}`} data-field={column.label}>
                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "acoes" && (
                        <div className="acoes-oficina text-center display-flex items-center justify-center gap-2">
                          {hasCriticalAlert(asset.id) && (
                            <SimpleTooltip content={`${getCriticalAlertCount(asset.id)} ${intl.formatMessage({ id: "intl-msg:alerts.criticalTooltip" })}`}>
                              <span 
                                className="rioglyph rioglyph-warning-sign text-color-danger cursor-pointer" 
                                style={{ fontSize: '18px' }}
                                onClick={() => handleAlertSidebar(asset.id)}
                              />
                            </SimpleTooltip>
                          )}
                          {hasCampaign(asset) ? (
                            <div className="cursor-pointer" style={{ position: 'relative' }} onClick={() => handleCampaignsSidebar(asset)}>
                              <span className="rioglyph rioglyph-calendar-today" />
                              {(asset?.campaigns?.number ?? 0) > 0 && (
                                <span className="alert-info">
                                  {asset?.campaigns?.number}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="rioglyph rioglyph-calendar-today" />
                            </div>
                          )}
                        </div>
                      )}
                      {column.value === "revisoes" &&
                        (asset.schedulings?.previous ? (
                          <div className="revisoes-periodicas text-center">
                            <button
                              type="button"
                              className="btn btn-primary btn-action text-size-22"
                              onClick={() => handlePeriodicReviewSidebar(asset)}
                            >
                              <span
                                className="rioglyph rioglyph-book"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </div>
                        ) : (
                          <div className="animate-pulse text-center">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        ))}

                      {column.value === "score" && (
                        <div className="text-center text-color-light">-</div>
                      )}

                      {column.value === "grupoManutencao" && (
                        <div className="flex items-center gap-4">
                          {
                            asset.groupName === '-' ?
                            <>
                              <span className="rioglyph rioglyph-warning-sign text-[#FF8E3C]"></span>
                              <p className="text-[#FF8E3C] truncate">{intl.formatMessage({ id: "intl-msg:maintenanceGroupSidebar.edit.undefined" })}</p>
                              <span 
                                onClick={() => {
                                  setMaintenanceGroupSidebarAsset(asset)
                                  setShowMaintenanceGroupSidebarDialog(true)
                                }} 
                                className="rioglyph cursor-pointer rioglyph-pencil ml-3 text-3xl text-[#30B4C0]"
                              ></span>
                            </> : <p className="truncate cursor-pointer" onClick={() => {
                              setMaintenanceGroupSidebarAsset(asset)
                              setShowMaintenanceGroupSidebarDialog(true)
                            }}>{getMaintenanceGroupLabelByValue(asset.groupName)}</p>
                          }
                        </div>
                      )}

                      {column.value === "status" && (
                        <div className="truncate">
                          <span
                            className={["Online", "Conectado"].includes(
                              props.assetsConnectionStatus.find(
                                (el) => el.assetId === asset.id
                              )?.connection ?? ""
                            )
                              ? "rioglyph rioglyph-wifi text-color-status-available padding-right-5"
                              : ["Offline", "Desconectado"].includes(
                                props.assetsConnectionStatus.find(
                                  (el) => el.assetId === asset.id
                                )?.connection ?? ""
                              )
                                ? "rioglyph rioglyph-wifi-off padding-right-5"
                                : undefined}
                          ></span>
                          {["Online", "Conectado"].includes(
                            props.assetsConnectionStatus.find(
                              (el) => el.assetId === asset.id
                            )?.connection ?? ""
                          )
                            ? intl.formatMessage({ id: "intl-msg:monitoring.status.connected" })
                            : intl.formatMessage({ id: "intl-msg:monitoring.status.notConnected" })}
                        </div>
                      )}

                      {column.value === "rio" && renderRioPlan(asset)}

                      {column.value === "statusGarantia" && (
                        <div className="status-garantia display-grid place-items-center">
                          <span
                            className={[
                              "Em Garantia",
                              "In Warranty",
                              "En garantia",
                              "En garantía",
                            ].includes(asset.warrantyStatus ?? "")
                              ? "text-color-garantia"
                              : "text-color-expirada"}
                          >
                            {[
                              "Em Garantia",
                              "In Warranty",
                              "En garantia",
                              "En garantía",
                            ].includes(asset.warrantyStatus ?? "")
                              ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.inWarranty" })
                              : [
                                "Expirado",
                                "Fora de Garantia",
                                "Fora de Garantía",
                                "Out of Warranty",
                                "Fuera de garantia",
                                "Fuera de garantía",
                                "Expired",
                              ].includes(asset.warrantyStatus ?? "")
                                ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.outOfWarranty" })
                                : asset.warrantyStatus}
                          </span>
                        </div>
                      )}

                      {column.value === "volksTotal" && (
                        <div className="volks-total text-center">
                          {(asset.hasTruckerPayPlan || asset.hasVolksEssentialsPlan) ? (
                            asset.accountName ? <div
                              className="p-4 text-color-gray text-decoration-underline cursor-pointer"
                              onClick={() => {
                                setContractDetailsAsset(asset);
                                setShowContractDetailsDialog(true);
                              }}
                            >
                              {asset.hasTruckerPayPlan ? "Trucker|Pay" :
                                asset.hasVolksEssentialsPlan ? "Volks|Total Essencial" :
                                  ""} 
                            </div> : <div className="animate-pulse"><div className="w-3/4 h-4 bg-gray-300 rounded" /></div>
                          ) : asset.volksTotalPlan !== "" ? (
                            <span className="text-color-gray">
                              {asset.volksTotalPlan}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => window.open("https://latam-marketplace.rio.cloud/customerCenter/services", "_blank")}
                            >
                              <span
                                className="rioglyph rioglyph-shopping-cart"
                                aria-hidden="true"
                              ></span>
                            </button>
                          )}
                        </div>
                      )}

                      {column.value === "ultimaRevisao" && (() => {
                        const lastReview = getLastReview(asset);
                        return (
                          <div className="ultima-revisao">
                            {lastReview ? (
                              <div
                                className="display-flex flex-column"
                                key={assetIndex}
                              >
                                <span className="flex items-center justify-between">
                                  {getSchedulingCheckupDisplayName(lastReview, intl.formatMessage)}
                                  <span
                                    className="rioglyph-ok-circle text-color-status-available"
                                  ></span>
                                </span>
                                <span className="text-color-info">
                                  {`Realizada em ${lastReview.date?.toLocaleDateString("pt-BR")}`}
                                </span>
                              </div>
                            ) : (
                              <div className="text-center">
                                <span className="text-color-secondary">-</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {column.value === "proximaRevisao" && (
                        <div className="proxima-revisao">
                          <NextScheduling
                            asset={asset}
                            hasMetrics={assetHasMetrics(
                              asset,
                              props.uptimeAssets
                            )} />
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

        <ExportProgressDialog
          show={progressOpen}
          stage={progressStage}
          progress={progressPct}
          detail={progressDetail}
          errorMessage={progressError}
          onClose={() => setProgressOpen(false)}
        />

        {alertSidebar && (
          <VehicleAlerts
            alerts={assetAlerts}
            closeSidebar={handleCloseAlertSidebar}
          />
        )}

        <ManualDialog
          show={assetManualsDialog}
          chassis={assetManualsChassis}
          close={handleCloseAssetManualsSidebar}
          manuals={assetManuals}
          loading={assetManualsLoading}
        />

        {campaignSidebar && (
          <Inspections
            inspectionsData={{ list: campaigns?.list }}
            closeSidebar={handleCloseCampaignSidebar}
            assetId={campaignsAssetId!}
            userRole="TOWER"
          />
        )}

        {(() => {
          return periodicReviewSidebar && (
            <PeriodicReviews
              dialog={periodicReviewSidebar}
              asset={assetPeriodicReviews}
              closeSidebar={handleClosePeriodicReviewSidebar}
              scheduleIds={[
                ...(assetPeriodicReviews?.schedulings?.next?.map(
                  (review) => review.id
                ) ?? []),
                ...(assetPeriodicReviews?.schedulings?.previous?.map(
                  (review) => review.id
                ) ?? []),
              ]}
              userRole="TOWER"
            />
          );
        })()}
      </div>
    );
  }

  const renderStickyHeader = () => (
    showStickyHeader && (
      <div
        className="fixed tables-row sticky-header"
        style={{
          top: '50px',
          zIndex: 6,
          backgroundColor: '#f8f9fa',
          transition: 'opacity 0.1s ease-out',
          opacity: 1,
          left: fixedTableContainerRef.current?.getBoundingClientRect().left ?? 0,
          width: fixedTableWidth > 0 ? fixedTableWidth : getStickyHeaderWidth(windowWidth, props.isSidebarOpen),
        }}
      >
        <div className=" sticky-header-fixed">
          <table className="table table-layout-fixed table-bordered">
            <colgroup>
              {fixedColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead>
              <tr>
                {fixedColumns.map((column) =>
                  renderTableHead(
                    column.value,
                    tableLabels[column.value as keyof typeof tableLabels],
                    "veiculo",
                    () => { }
                  )
                )}
              </tr>
            </thead>
          </table>
        </div>
      </div>
    )
  );
  
  const desktopRender = () => (
    <div id="TableCommonDemo">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <div className="flex gap-10">
                <button
                  type="button"
                  className="btn bg-highlight-darker padding-7"
                  onClick={() =>
                    navigate(`/control-tower/appointments/appointment`)
                  }
                >
                  <span className="text-capitalize padding-right-5">
                    {intl.formatMessage({ id: "intl-msg:monitoring.createScheduling" })}
                  </span>
                  <span
                    className="rioglyph rioglyph-plus-sign"
                    aria-hidden="true"
                  />
                </button>

                {/*<button
                  className="btn bg-highlight-darker flex items-center justify-center"
                  onClick={exportToExcel}
                  disabled={!props.assets?.length}
                >
                  <span className="rioglyph rioglyph-download !text-[20px] !m-[0]"></span>
                </button>*/}
              </div>
            </div>
          </div>
        </div>
      </TableToolbar>

      {/* Renderiza o sticky header scrollável */}
      {renderStickyScrollableHeader()}

      {/* Renderiza o sticky header fixo */}
      {renderStickyHeader()}

      <div className="tables-row">
        <div className="tables-row__fixed" ref={fixedTableContainerRef}>
          <table className="table table-layout-fixed table-bordered">
            <colgroup>
              {fixedColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead ref={fixedTableHeaderRef}>
              <tr>
                {fixedColumns.map((column) =>
                  renderTableHead(
                    column.value,
                    tableLabels[column.value as keyof typeof tableLabels],
                    "veiculo",
                    () => { }
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {props.assets.map((asset: Asset, assetIndex: number) => (
                <tr key={assetIndex}>
                  {fixedColumns.map((column, colIndex) => (
                    <td
                      key={`${column.value}-${assetIndex}-${colIndex}`}
                      data-field={column.label}
                    >
                      {renderSchedulingData(column.value, asset)}

                      {column.value === "veiculo" && (
                        <div className="warning-vehicle">
                          <span className="vehicle-name" title={asset.name}>
                            {asset.name ?? asset.model}
                          </span>
                          {hasAlert(asset.id) ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-action text-size-h6"
                              onClick={() => handleAlertSidebar(asset.id)}
                            >
                              <span
                                className="rioglyph rioglyph-warning-sign"
                                aria-hidden="true"
                              ></span>
                            </button>
                          ) : null}
                        </div>
                      )}

                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "licensePlate" && (
                        <div>
                          {asset.licensePlate === '-' ? (
                            <span>-</span> 
                          ) : asset.licensePlate ? (
                            <SimpleTooltip content={asset.licensePlate} placement="top">
                              <div className="truncate">{asset.licensePlate}</div>
                            </SimpleTooltip>
                          ) : (
                            <div className="animate-pulse">
                              <div className="w-3/4 h-4 bg-gray-300 rounded" />
                            </div> 
                          )}
                        </div>
                      )}

                      {column.value === "accountName" &&
                        (asset.accountName ? (
                          <SimpleTooltip
                            content={asset.accountName}
                            placement="top"
                          >
                            <div className="line-clamp-3">
                              {asset.accountName}
                            </div>
                          </SimpleTooltip>
                        ) : (
                          <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className={`scrollable-table original-scrollable-table ${scrolledClass ? "scrolled-ativo" : ""}`}
          ref={scrollableTableRef}
          data-scrolled={scrolledClass}
          style={fixedTableWidth > 0 ? { marginLeft: fixedTableWidth + 8 } : undefined}
        >
          <table
            className="table table-layout-fixed table-column-overflow-hidden table-bordered table-head-filled"
            style={{ minWidth: "1650px" }}
          >
            <colgroup>
              {scrollableColumns.map((column) =>
                renderTableCaption(column.value, column.details)
              )}
            </colgroup>
            <thead ref={scrollableTableHeaderRef}>
              <tr>
                {scrollableColumns.map((column) =>
                  renderTableHead(
                    column.value,
                    tableLabels[column.value as keyof typeof tableLabels],
                    "veiculo",
                    () => { }
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {props.assets.map((asset: Asset, assetIndex: number) => (
                <tr key={assetIndex}>
                  {scrollableColumns.map((column) => (
                    <td key={column.value}>
                      {renderChassisData(column.value, asset)}
                      {renderOdometerData(column.value, asset)}
                      {renderHourmeterData(column.value, asset)}

                      {column.value === "acoes" ? (
                        hasCampaign(asset) ? (
                          <div
                            className="acoes-oficina cursor-pointer text-center"
                            onClick={() => handleCampaignsSidebar(asset)}
                          >
                            <div style={{ position: 'relative' }}>
                              <span className="rioglyph rioglyph-calendar-today" />
                              {(asset?.campaigns?.number ?? 0) > 0 && (
                                <span className="alert-info">
                                  {asset?.campaigns?.number}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="acoes-oficina cursor-pointer text-center">
                            <span className="rioglyph rioglyph-calendar-today" />
                          </div>
                        )
                      ) : null}

                      {column.value === "revisoes" &&
                        (asset.schedulings?.previous ? (
                          <div className="revisoes-periodicas text-center">
                            <button
                              type="button"
                              className="btn btn-primary btn-action text-size-22"
                              onClick={() => handlePeriodicReviewSidebar(asset)}
                            >
                              <span
                                className="rioglyph rioglyph-book"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </div>
                        ) : (
                          <div className="animate-pulse">
                            <div className="w-3/4 h-4 bg-gray-300 rounded" />
                          </div>
                        ))}

                      {column.value === "score" && (
                        <div className="text-center text-color-light">-</div>
                      )}

                      {column.value === "grupoManutencao" && (
                        <div className="flex items-center gap-4">
                          {
                            asset.groupName === '-' ?
                            <>
                              <span className="rioglyph rioglyph-warning-sign text-[#FF8E3C]"></span>
                              <p className="text-[#FF8E3C] truncate">{intl.formatMessage({ id: "intl-msg:maintenanceGroupSidebar.edit.undefined" })}</p>
                              <span 
                                onClick={() => {
                                  setMaintenanceGroupSidebarAsset(asset)
                                  setShowMaintenanceGroupSidebarDialog(true)
                                }} 
                                className="rioglyph cursor-pointer rioglyph-pencil ml-3 text-3xl text-[#30B4C0]"
                              ></span>
                            </> : <p className="truncate cursor-pointer" onClick={() => {
                              setMaintenanceGroupSidebarAsset(asset)
                              setShowMaintenanceGroupSidebarDialog(true)
                            }}>{getMaintenanceGroupLabelByValue(asset.groupName)}
                            </p>
                          }
                        </div>
                      )}

                      {column.value === "status" && (
                        <div className="truncate">
                          <span
                            className={
                              ["Online", "Conectado"].includes(
                                props.assetsConnectionStatus.find(
                                  (el) => el.assetId === asset.id
                                )?.connection ?? ""
                              )
                                ? "rioglyph rioglyph-wifi text-color-status-available padding-right-5"
                                : ["Offline", "Desconectado"].includes(
                                  props.assetsConnectionStatus.find(
                                    (el) => el.assetId === asset.id
                                  )?.connection ?? ""
                                )
                                  ? "rioglyph rioglyph-wifi-off padding-right-5"
                                  : undefined
                            }
                          ></span>
                          {["Online", "Conectado"].includes(
                            props.assetsConnectionStatus.find(
                              (el) => el.assetId === asset.id
                            )?.connection ?? ""
                          )
                            ? intl.formatMessage({ id: "intl-msg:monitoring.status.connected" })
                            : intl.formatMessage({ id: "intl-msg:monitoring.status.notConnected" })}
                        </div>
                      )}

                      {column.value === "rio" && renderRioPlan(asset)}

                      {column.value === "statusGarantia" && (
                        <div className="status-garantia display-grid place-items-center">
                          <span
                            className={
                              [
                                "Em Garantia",
                                "In Warranty",
                                "En garantia",
                                "En garantía",
                              ].includes(asset.warrantyStatus ?? "")
                                ? "text-color-garantia"
                                : "text-color-expirada"
                            }
                          >
                            {[
                              "Em Garantia",
                              "In Warranty",
                              "En garantia",
                              "En garantía",
                            ].includes(asset.warrantyStatus ?? "")
                              ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.inWarranty" })
                              : [
                                "Expirado",
                                "Fora de Garantia",
                                "Fora de Garantía",
                                "Out of Warranty",
                                "Fuera de garantia",
                                "Fuera de garantía",
                                "Expired",
                              ].includes(asset.warrantyStatus ?? "")
                                ? intl.formatMessage({ id: "intl-msg:monitoring.warrantyStatus.outOfWarranty" })
                                : asset.warrantyStatus}
                          </span>
                        </div>
                      )}

                      {column.value === "volksTotal" && (
                        <div className="volks-total text-center">
                          {(asset.hasTruckerPayPlan || asset.hasVolksEssentialsPlan) ? (
                            asset.accountName ?<div
                              className="p-4 text-color-gray text-decoration-underline cursor-pointer"
                              onClick={() => {
                                setContractDetailsAsset(asset);
                                setShowContractDetailsDialog(true);
                              }}
                            >
                              {asset.hasTruckerPayPlan ? "Trucker|Pay" : 
                                asset.hasVolksEssentialsPlan ? "Volks|Total Essencial" :
                                  ""} 
                            </div> : <div className="animate-pulse"><div className="w-3/4 h-4 bg-gray-300 rounded" /></div>
                          ) : asset.volksTotalPlan !== "" ? (
                            <span className="text-color-gray">
                              {asset.volksTotalPlan}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() =>
                                window.open(
                                  "https://latam-marketplace.rio.cloud/customerCenter/services",
                                  "_blank"
                                )
                              }
                            >
                              <span
                                className="rioglyph rioglyph-shopping-cart"
                                aria-hidden="true"
                              ></span>
                            </button>
                          )}
                        </div>
                      )}

                      {column.value === "ultimaRevisao" && (() => {
                        const lastReview = getLastReview(asset);
                        return (
                          <div className="ultima-revisao">
                            {lastReview ? (
                              <div
                                className="display-flex flex-column"
                                key={assetIndex}
                              >
                                <span className="flex items-center justify-between">
                                  {getSchedulingCheckupDisplayName(lastReview, intl.formatMessage)}
                                  <span
                                    className="rioglyph-ok-circle text-color-status-available"
                                  ></span>
                                </span>
                                <span className={`truncate text-color-info`}>
                                  {`Realizada em ${lastReview.date?.toLocaleDateString("pt-BR")}`}
                                </span>
                              </div>
                            ) : (
                              <div className="text-center">
                                <span className="text-color-secondary">-</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {column.value === "proximaRevisao" && (
                        <div className="proxima-revisao">
                          <NextScheduling
                            asset={asset}
                            hasMetrics={assetHasMetrics(
                              asset,
                              props.uptimeAssets
                            )}
                          />
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  return (
    <>
      {desktopRender()}
      {renderModals()}
    </>
  );
};

export default MaintenanceTable;